import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { adminSubscriptionsAPI } from '../../../lib/api';
import { useToast } from '../../../hooks/use-toast';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';

function formatDeletedAt(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const SubscriptionTrash = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringKey, setRestoringKey] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await adminSubscriptionsAPI.getTrash();
      setItems(rows || []);
    } catch (error) {
      toast({ title: 'Ошибка загрузки корзины', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const restore = async (row) => {
    const key = `${row.entity_type}:${row.id}`;
    if (!window.confirm(`Восстановить «${row.title}»?`)) return;
    setRestoringKey(key);
    try {
      await adminSubscriptionsAPI.restoreTrashItem(row.entity_type, row.id);
      toast({ title: 'Запись восстановлена' });
      await load();
    } catch (error) {
      toast({ title: 'Не удалось восстановить', description: error.message, variant: 'destructive' });
    } finally {
      setRestoringKey('');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Корзина</CardTitle>
        <CardDescription>
          Удалённые записи из раздела подписок. Полного удаления нет — только восстановление.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Загрузка…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Корзина пуста</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Тип</TableHead>
                <TableHead>Запись</TableHead>
                <TableHead>Удалено</TableHead>
                <TableHead className="w-[120px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => {
                const key = `${row.entity_type}:${row.id}`;
                return (
                  <TableRow key={key}>
                    <TableCell>{row.entity_label}</TableCell>
                    <TableCell>{row.title}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatDeletedAt(row.deleted_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={restoringKey === key}
                        onClick={() => restore(row)}
                      >
                        <RotateCcw className="mr-1 h-3.5 w-3.5" />
                        Восстановить
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionTrash;
